<template>
    <tile id="big-tile" :class="{'dark-pic' : isDark}">
        <div class="h-full w-full flex items-center justify-between">
            <div
                v-if="onboardingCompletedDefault"
                class="rounded-lg h-full p-4 flex flex-col gap-1  w-full"
                :class="{
                    'bg-black/70': isDark,
                    'bg-white/80': !isDark,
                }"
            >
                <div class="flex items-start justify-between">
                    <div class="text-2xl mb-2 text-primary font-semibold">
                        {{ selectedOrg?.organizationName }}
                    </div>

                    <UButton
                        color="neutral"
                        variant="outline"
                        class="ml-auto"
                        icon="i-ph-rocket-launch"
                        @click="onboardingCompleted.value = false"
                    >
                        Åpne guide
                    </UButton>
                </div>
                <div>{{ selectedOrg?.organizationNumber }}</div>
                <div class="grow" />
                <div class="text-lg flex justify-between items-baseline">
                    <div>Bilag i {{ selectedYear }}</div>
                    <div class="text-xl">{{ transactionCount }}</div>
                </div>
                <div class="text-lg flex justify-between items-baseline">
                    <div>Bilag i {{ selectedYear - 1 }}</div>
                    <div class="text-xl mr-0.5">{{ previousYearTransactionCount }}</div>
                </div>
            </div>

            <OnboardingGuide v-else />
        </div>
    </tile>
</template>

<script setup lang="ts">

import Tile from '@/dashboard/Tile.vue'
import {isDark} from '@/theme/theme.service'
import {selectedOrg} from '@/tenants/org.service'
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {countTransactions} from '@/transaction/transaction.service'
import {format} from 'date-fns'
import {TransactionQueryParams} from '@/types/transaction/TransactionQueryParams'
import {useEmitter} from '@/services/infrastructure/emitter'
import {TRANSACTION_CREATED} from '@/types/transaction/TransactionEvent'
import {ORG_SWITCHED} from '@/types/events'
import {useSetting} from '@/setting/setting.service'
import OnboardingGuide from '@/dashboard/tiles/OnboardingGuide.vue'
// import {getUser} from '@/services/infrastructure/auth.service'

const {onEvent, cleanUp} = useEmitter()
const transactionCount = ref(0)
const previousYearTransactionCount = ref(0)

const selectedYear = new Date().getFullYear()

const {setting: onboardingCompleted} = useSetting<boolean>('ONBOARDING_ORGANIZATION_COMPLETE')

const onboardingCompletedDefault = computed(() => {
    return onboardingCompleted.value?.value ?? true
})

const toDateString = (d: Date) => {
    return format(d, 'yyyy-MM-dd')
}

const fetchTransactionCounts = async () => {
    const yearToDateQuery: TransactionQueryParams = {
        fromDate: toDateString(
            new Date(selectedYear, 0, 1)
        ),
        toDate: toDateString(
            new Date(selectedYear, 11, 31)
        )
    }
    countTransactions(yearToDateQuery).then(count => {
        transactionCount.value = count ?? 0
    })

    const lastYearQuery: TransactionQueryParams = {
        fromDate: toDateString(
            new Date(selectedYear - 1, 0, 1)
        ),
        toDate: toDateString(
            new Date(selectedYear - 1, 11, 31)
        )
    }
    countTransactions(lastYearQuery).then(count => {
        previousYearTransactionCount.value = count ?? 0
    })
}

onMounted(() => {
    fetchTransactionCounts()
    onEvent(TRANSACTION_CREATED, fetchTransactionCounts)
    onEvent(ORG_SWITCHED, fetchTransactionCounts)
})

onUnmounted(cleanUp)

</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";


#big-tile {
    background-image: url('/panel-pic-bg-light.jpg');
    background-size: cover;

    &.dark-pic {
        background-image: url('/panel-pic-bg-dark-new.jpg');
    }

    .company-logo-container {
        background: linear-gradient(90deg, rgba(0, 142, 179, 0) 0%, rgb(0, 142, 179) 50%);
        box-shadow: 14px 4px 8px rgba(0, 0, 0, 0.2);
    }
}

</style>